import React, { ReactElement } from "react";
import { useHistory } from "react-router-dom";

type ChatLinkifyMessageProps = {
  content: string;
  contentPayload?: any;
  isMobile?: boolean;
  isPlainText?: boolean;
  onClickLink?: (e: React.MouseEvent) => void;
  onNavigationMessage?: (data: any) => ReactElement;
};

// Regex สำหรับ markdown links: [text](url)
const MARKDOWN_LINK_REGEX = /\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g;

// Regex สำหรับ plain URL
const PLAIN_URL_REGEX = /https?:\/\/[^\s<>]+[^\s<>.,;:!?)\])]*/g;

export const hasMarkdownPattern = (content: string): boolean =>
  /\[.+?\]\(https?:\/\/[^\s)]+\)/.test(content);

export const hasPlainUrl = (content: string): boolean => /https?:\/\/[^\s<>]+/.test(content);

const ChatLinkifyMessage = (props: ChatLinkifyMessageProps) => {
  const history = useHistory();

  // เลือก regex และ detection function ตาม isPlainText
  const regex = props.isPlainText ? PLAIN_URL_REGEX : MARKDOWN_LINK_REGEX;
  const hasLinks = props.isPlainText
    ? hasPlainUrl(props.content)
    : hasMarkdownPattern(props.content);

  const handleLinkClick = (e: React.MouseEvent, url: string) => {
    if (props.isMobile) {
      e.preventDefault();

      if (props.isPlainText) {
        const urlObj = new URL(url);
        urlObj.searchParams.set("openExternalBrowser", "1");
        const modifiedUrl = urlObj.toString();
        window.location.href = modifiedUrl;
        return;
      }

      // สำหรับ markdown ใช้ history.push
      const path = url.startsWith("http") ? new URL(url).pathname + new URL(url).search : url;

      history.push(path);
    }

    props.onClickLink?.(e);
  };

  // ถ้าไม่มี links
  if (!hasLinks) {
    // สำหรับ plain text ให้ return ข้อความเลย
    if (props.isPlainText) {
      return <>{props.content}</>;
    }

    // สำหรับ markdown ใช้ onNavigationMessage (ถ้ามี)
    if (props.onNavigationMessage) {
      return props.onNavigationMessage({
        contentPayload: props.contentPayload,
        contentType: "text_hyper_link",
        content: props.content,
      });
    }

    // 🆕 ถ้าไม่มี onNavigationMessage ให้ return ข้อความธรรมดา
    return <>{props.content}</>;
  }

  // Parse links using matchAll
  const matches = [...props.content.matchAll(regex)];

  if (matches.length === 0) {
    return <>{props.content}</>;
  }

  const parts: ReactElement[] = [];
  let lastIndex = 0;

  for (const match of matches) {
    const index = match.index ?? 0;

    // แยก logic สำหรับ plain text และ markdown
    const isPlainText = props.isPlainText;
    const fullMatch = match[0];
    const text = isPlainText ? fullMatch : match[1];
    const url = isPlainText ? fullMatch : match[2];

    // Add text before the link
    if (index > lastIndex) {
      parts.push(
        <React.Fragment key={`text-${lastIndex}`}>
          {props.content.slice(lastIndex, index)}
        </React.Fragment>
      );
    }

    // Add the link
    parts.push(
      <a
        key={`link-${index}`}
        style={{
          color: "#2185d0",
          textDecoration: "underline",
          ...(isPlainText && { position: "relative" }),
        }}
        href={url}
        rel="noopener noreferrer"
        target="_blank"
        onClick={(e) => handleLinkClick(e, url)}
        {...(isPlainText && {
          onMouseDown: (e) => handleLinkClick(e, url),
          onContextMenu: (e) => handleLinkClick(e, url),
        })}
      >
        {text}
      </a>
    );

    lastIndex = index + fullMatch.length;
  }

  // Add remaining text after last link
  if (lastIndex < props.content.length) {
    parts.push(
      <React.Fragment key={`text-${lastIndex}`}>{props.content.slice(lastIndex)}</React.Fragment>
    );
  }

  return <>{parts}</>;
};

export default ChatLinkifyMessage;
